import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import WebSocketService from '../services/WebSocketService';
import '../App.css';
import { getWebSocketUrl, getAuth0AccessToken } from '../utils/api';
import { playClickSound } from '../utils/sounds';


function ProfilePage({ viewMode }) {
  const { user, isAuthenticated, getAccessTokenSilently, logout } = useAuth0();

  // Initialize with cached data if available to prevent UI flashing
  const [username, setUsername] = useState(() => {
    if (isAuthenticated) {
      return localStorage.getItem('blockRivalsUsername') || '';
    } else {
      return sessionStorage.getItem('blockRivalsSessionGuestName') || '';
    }
  });

  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editedUsername, setEditedUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');

  const [stats, setStats] = useState(() => {
    const savedStats = localStorage.getItem('blockRivalsStats');
    return savedStats ? JSON.parse(savedStats) : { wins: 0, losses: 0, rating: 1000 };
  });

  const [hasChangedUsername, setHasChangedUsername] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Restore cached nicknameset flag IMMEDIATELY when user becomes available
  // Use useLayoutEffect to run synchronously before paint, preventing button flash
  useLayoutEffect(() => {
    if (isAuthenticated && user?.sub) {
      const profileCacheKey = `blockRivalsProfileLoaded_${user.sub}`;
      const profileLoaded = sessionStorage.getItem(profileCacheKey);

      console.log('🔄 Restore cache useLayoutEffect running. profileLoaded:', profileLoaded);

      // Only restore cached nicknameset if we have a cached profile
      if (profileLoaded) {
        const cachedNicknameSet = sessionStorage.getItem(`blockRivalsNicknameSet_${user.sub}`);
        console.log('📦 Found cached nicknameset:', cachedNicknameSet);
        if (cachedNicknameSet !== null) {
          console.log('✏️ Restoring hasChangedUsername to:', cachedNicknameSet === 'true');
          setHasChangedUsername(cachedNicknameSet === 'true');
        }
      } else {
        console.log('⏭️ No cached profile, keeping hasChangedUsername as false');
      }
      setCacheChecked(true);
    } else if (!isAuthenticated) {
      // For guests, no need to check cache
      setCacheChecked(true);
    }
  }, [isAuthenticated, user?.sub]);

  // Listen for rating updates from game results
  useEffect(() => {
    const handleRatingUpdate = (event) => {
      console.log('📊 Rating update event received:', event.detail);
      const { rating, wins, losses } = event.detail;

      // Use functional update to avoid stale closure
      setStats(prevStats => {
        const newStats = {
          wins: wins !== undefined ? wins : prevStats.wins,
          losses: losses !== undefined ? losses : prevStats.losses,
          rating: rating !== undefined ? rating : prevStats.rating
        };

        localStorage.setItem('blockRivalsStats', JSON.stringify(newStats));
        console.log('✅ Stats updated from game result:', newStats);
        return newStats;
      });
    };

    console.log('🎧 ProfilePage: Setting up ratingUpdated event listener');
    window.addEventListener('ratingUpdated', handleRatingUpdate);
    return () => {
      console.log('🔇 ProfilePage: Removing ratingUpdated event listener');
      window.removeEventListener('ratingUpdated', handleRatingUpdate);
    };
  }, []); // Empty dependency array - listener stays active for component lifetime

  useEffect(() => {
    const loadProfile = async () => {
      // For guest users, use session-specific Guest name and localStorage stats
      if (!isAuthenticated) {
        // Check if guest profile already initialized this session
        const guestInitialized = sessionStorage.getItem('blockRivalsGuestInitialized');
        if (guestInitialized) {
          console.log('Guest profile already loaded, skipping...');
          return;
        }

        console.log('Loading guest profile...');
        let sessionGuestName = sessionStorage.getItem('blockRivalsSessionGuestName');
        if (!sessionGuestName) {
          sessionGuestName = `Guest${Math.floor(Math.random() * 10000)}`;
          sessionStorage.setItem('blockRivalsSessionGuestName', sessionGuestName);
        }
        setUsername(sessionGuestName);

        const savedStats = localStorage.getItem('blockRivalsStats');
        if (savedStats) {
          setStats(JSON.parse(savedStats));
        }
        sessionStorage.setItem('blockRivalsGuestInitialized', 'true');
        return;
      }

      // For authenticated users, check if we've loaded profile data this session
      const profileCacheKey = `blockRivalsProfileLoaded_${user?.sub}`;
      const profileLoaded = sessionStorage.getItem(profileCacheKey);

      if (profileLoaded) {
        console.log('Authenticated profile already loaded from server this session, using cache...');

        // Load stats from localStorage (updated by game results)
        const savedStats = localStorage.getItem('blockRivalsStats');
        if (savedStats) {
          const parsedStats = JSON.parse(savedStats);
          console.log('📊 Loading stats from localStorage:', parsedStats);
          setStats(parsedStats);
        }

        // Load username from localStorage
        const savedUsername = localStorage.getItem('blockRivalsUsername');
        if (savedUsername) {
          setUsername(savedUsername);
        }

        // Restore nicknameset flag from cache
        const cachedNicknameSet = sessionStorage.getItem(`blockRivalsNicknameSet_${user?.sub}`);
        console.log('📋 Restoring from cached nicknameset:', cachedNicknameSet);
        setHasChangedUsername(cachedNicknameSet === 'true');
        return;
      }

      console.log('Loading authenticated user profile...');
      setLoadingProfile(true);

      // Set timeout fallback to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.warn('Profile loading timed out, using localStorage fallback');
        const savedUsername = localStorage.getItem('blockRivalsUsername');
        if (savedUsername) {
          setUsername(savedUsername);
        }
        const savedStats = localStorage.getItem('blockRivalsStats');
        if (savedStats) {
          setStats(JSON.parse(savedStats));
        }
        setHasChangedUsername(false);
        setLoadingProfile(false);
        sessionStorage.setItem(`blockRivalsProfileLoaded_${user?.sub}`, 'true');
      }, 5000); // 5 second timeout

      try {
        const authToken = await getAuth0AccessToken(getAccessTokenSilently);
        const wsUrl = getWebSocketUrl();

        // Set up one-time Identified listener
        const handleIdentified = (message) => {
          clearTimeout(timeoutId); // Clear timeout since we got a response

          const { body } = message;
          console.log('Profile loaded from server:', body);

          if (body && body.accepted && body.guest === false) {
            // Authenticated user with profile
            const newStats = {
              wins: body.wins || 0,
              losses: body.losses || 0,
              rating: body.rating || 1000
            };
            setStats(newStats);
            // Update localStorage cache for stats
            localStorage.setItem('blockRivalsStats', JSON.stringify(newStats));

            // Update username from server nickname
            if (body.nickname) {
              setUsername(body.nickname);
              // Also save to localStorage for display elsewhere
              localStorage.setItem('blockRivalsUsername', body.nickname);
              // Dispatch event to notify other components
              window.dispatchEvent(new CustomEvent('usernameUpdated', { detail: body.nickname }));
            }

            // Use nicknameset flag from server response (source of truth)
            const nicknameSet = body.nicknameset === true;
            console.log('🔍 Setting hasChangedUsername to:', nicknameSet, '(from body.nicknameset:', body.nicknameset, ')');
            setHasChangedUsername(nicknameSet);

            // Always update cache with server's response (server is source of truth)
            // This will overwrite any stale cached values
            sessionStorage.setItem(`blockRivalsNicknameSet_${user?.sub}`, String(nicknameSet));
            console.log('💾 Cached nicknameset in sessionStorage:', String(nicknameSet));
          } else if (body && body.accepted) {
            // Backend bug: authenticated users are being returned as guests
            // Fallback to localStorage until backend is fixed
            console.warn('Backend returned guest=true for authenticated user - using localStorage fallback');
            const savedUsername = localStorage.getItem('blockRivalsUsername');
            if (savedUsername) {
              setUsername(savedUsername);
            } else {
              // If no saved username, use Auth0 email as fallback
              setUsername(user?.email || user?.name || 'User');
            }
            const savedStats = localStorage.getItem('blockRivalsStats');
            if (savedStats) {
              setStats(JSON.parse(savedStats));
            }
            // Assume username not changed if we can't get server data
            setHasChangedUsername(false);
          } else {
            console.error('Unexpected Identified response:', body);
          }

          WebSocketService.off('Identified', handleIdentified);
          WebSocketService.disconnect();
          setLoadingProfile(false);
          sessionStorage.setItem(`blockRivalsProfileLoaded_${user?.sub}`, 'true');
        };

        WebSocketService.on('Identified', handleIdentified);
        await WebSocketService.connect(wsUrl);

        // Send Identify with token, leaving playerName blank (server will use saved nickname or Auth0 profile)
        // Use Auth0 sub as playerId
        WebSocketService.identify(user.sub, '', authToken);
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('Failed to load profile from server:', error);

        // Fallback to localStorage on error
        const savedUsername = localStorage.getItem('blockRivalsUsername');
        if (savedUsername) {
          setUsername(savedUsername);
        } else {
          // If no saved username, use Auth0 email as fallback
          setUsername(user?.email || user?.name || 'User');
        }
        const savedStats = localStorage.getItem('blockRivalsStats');
        if (savedStats) {
          setStats(JSON.parse(savedStats));
        }

        setLoadingProfile(false);
        sessionStorage.setItem(`blockRivalsProfileLoaded_${user?.sub}`, 'true');
      }
    };

    loadProfile();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  const handleEditUsername = () => {
    playClickSound();
    setEditedUsername(username);
    setUsernameError(''); // Clear any previous error
    setIsEditingUsername(true);
  };

  const handleSaveUsername = async () => {
    playClickSound();
    if (!editedUsername.trim()) return;

    const trimmedUsername = editedUsername.trim();

    // For authenticated users, send Identify to save nickname on server
    if (isAuthenticated) {
      setLoadingProfile(true);
      try {
        console.log('🔵 Starting username change process...');
        console.log('  New username:', trimmedUsername);
        console.log('  User ID:', user.sub);

        const authToken = await getAuth0AccessToken(getAccessTokenSilently);
        console.log('  Got auth token:', authToken ? 'YES' : 'NO');

        const wsUrl = getWebSocketUrl();
        console.log('  WebSocket URL:', wsUrl);

        // Set up one-time Identified listener
        const handleIdentified = (message) => {
          console.log('🟢 Received Identified response:', message);
          const { body } = message;
          console.log('  Response body.guest:', body?.guest);
          console.log('  Response body.accepted:', body?.accepted);
          console.log('  Response body.nickname:', body?.nickname);
          console.log('  Response body.nicknameset:', body?.nicknameset);
          console.log('  Full body:', JSON.stringify(body, null, 2));

          if (body && body.accepted === false) {
            // Username was rejected (already taken)
            console.log('❌ Username rejected - already taken');
            setUsernameError('This username is already taken');
            // Keep editing mode open so user can try again
            setLoadingProfile(false);
          } else if (body && body.accepted && body.guest === false) {
            console.log('✅ Nickname updated on server:', body.nickname);

            // Update local state with server response
            setUsername(body.nickname || trimmedUsername);
            localStorage.setItem('blockRivalsUsername', body.nickname || trimmedUsername);

            // Use nicknameset flag from server (source of truth)
            // If server doesn't provide it, assume true since we just saved
            const nicknameSet = body.nicknameset !== undefined ? body.nicknameset === true : true;
            console.log('🔒 Setting hasChangedUsername to:', nicknameSet);
            setHasChangedUsername(nicknameSet);

            // Clear loading state BEFORE closing edit mode to prevent "Loading..." flash
            setLoadingProfile(false);
            setIsEditingUsername(false);

            // Mark profile as loaded since we just got fresh data from server
            sessionStorage.setItem(`blockRivalsProfileLoaded_${user.sub}`, 'true');
            // Cache the nicknameset flag for this session
            sessionStorage.setItem(`blockRivalsNicknameSet_${user.sub}`, String(nicknameSet));
            console.log('💾 Cached nicknameset after save:', String(nicknameSet));

            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent('usernameUpdated', { detail: body.nickname || trimmedUsername }));

            // Also update stats if provided
            if (body.rating !== undefined) {
              const newStats = {
                wins: body.wins || 0,
                losses: body.losses || 0,
                rating: body.rating || 1000
              };
              setStats(newStats);
              // Update localStorage cache for stats too
              localStorage.setItem('blockRivalsStats', JSON.stringify(newStats));
            }
          } else {
            console.log('❌ Unexpected response:', body);
            alert('Failed to update username. Please try again.');
            setLoadingProfile(false);
          }

          WebSocketService.off('Identified', handleIdentified);
          WebSocketService.disconnect();
        };

        // Disconnect any existing connection first to ensure clean state
        WebSocketService.disconnect();

        // Remove ALL existing Identified listeners to prevent conflicts
        WebSocketService.listeners.delete('Identified');

        // Add a small delay to ensure disconnect completes
        await new Promise(resolve => setTimeout(resolve, 100));

        WebSocketService.on('Identified', handleIdentified);
        console.log('  Connecting to WebSocket...');
        await WebSocketService.connect(wsUrl);
        console.log('  Connected! Sending Identify message...');

        // Send Identify with new nickname
        WebSocketService.identify(user.sub, trimmedUsername, authToken);
        console.log('  Identify message sent!');
      } catch (error) {
        console.error('❌ Failed to save nickname to server:', error);
        alert('Failed to save username. Please try again.');
        setIsEditingUsername(false);
        setLoadingProfile(false);
      }
    } else {
      // For guests, just save locally
      setUsername(trimmedUsername);
      localStorage.setItem('blockRivalsUsername', trimmedUsername);
      setIsEditingUsername(false);
    }
  };

  const handleCancelEdit = () => {
    playClickSound();
    setIsEditingUsername(false);
    setEditedUsername('');
  };

  const handleDeleteAccount = async () => {
    playClickSound();
    if (!isAuthenticated) return;

    setDeletingAccount(true);
    try {
      const authToken = await getAuth0AccessToken(getAccessTokenSilently);
      const wsUrl = getWebSocketUrl();

      // Set up one-time Forgotten listener
      const handleForgotten = (message) => {
        const { body } = message;
        console.log('Forgotten response:', body);

        WebSocketService.off('Forgotten', handleForgotten);
        WebSocketService.disconnect();

        if (body && body.accepted) {
          // Clear local storage
          localStorage.removeItem('blockRivalsUsername');
          localStorage.removeItem('blockRivalsStats');
          localStorage.removeItem('blockrivalsGuestPlayerId');

          // Clear session storage cache flags
          if (user?.sub) {
            sessionStorage.removeItem(`blockRivalsProfileLoaded_${user.sub}`);
            sessionStorage.removeItem(`blockRivalsNicknameSet_${user.sub}`);
          }

          // Log out from Auth0 and redirect to home
          logout({ returnTo: window.location.origin });
        } else {
          alert(`Failed to delete account: ${body?.reason || 'Unknown error'}`);
          setDeletingAccount(false);
          setShowDeleteConfirm(false);
        }
      };

      WebSocketService.on('Forgotten', handleForgotten);
      await WebSocketService.connect(wsUrl);

      // Send Forget message with auth token
      WebSocketService.forget(authToken);
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('Failed to delete account. Please try again.');
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  const winRate = stats.wins + stats.losses > 0
    ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1)
    : 0;

  // Only show button as enabled/disabled after cache has been checked
  const canEditUsername = isAuthenticated && !hasChangedUsername && cacheChecked;

  console.log('🎯 Button state - isAuthenticated:', isAuthenticated, 'hasChangedUsername:', hasChangedUsername, 'cacheChecked:', cacheChecked, 'canEditUsername:', canEditUsername);

  return (
    <div className="profile-page">
      <h2>Player Profile</h2>

      <div className="profile-section">
        <h3>Username</h3>
        {isEditingUsername ? (
          <div className="username-edit">
            {usernameError && (
              <div className="username-error-message">
                {usernameError}
              </div>
            )}
            <input
              type="text"
              value={editedUsername}
              onChange={(e) => {
                setEditedUsername(e.target.value);
                setUsernameError(''); // Clear error when user types
              }}
              placeholder="Enter username"
              maxLength={20}
              className="username-input"
            />
            <div className="edit-buttons">
              <button
                onClick={handleSaveUsername}
                className="menu-btn primary small"
                disabled={loadingProfile}
              >
                {loadingProfile ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                className="menu-btn secondary small"
                disabled={loadingProfile}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="username-display">
            <p className="username-text">{loadingProfile ? 'Loading...' : username}</p>
            <button
              onClick={handleEditUsername}
              className="menu-btn secondary small"
              disabled={!canEditUsername || loadingProfile}
            >
              Edit Username
            </button>
          </div>
        )}

        {!isAuthenticated && (
          <p className="username-note">Log in to change your username</p>
        )}

        {isAuthenticated && hasChangedUsername && (
          <p className="username-note">Username can only be changed once</p>
        )}
      </div>

      <div className="profile-section">
        <h3>Match Statistics</h3>
        {loadingProfile ? (
          <p className="loading-text">Loading stats...</p>
        ) : (
          <div className="stats-grid">
            {isAuthenticated && (
              <div className="stat-item">
                <p className="stat-label">Rating</p>
                <p className="stat-value">{stats.rating}</p>
              </div>
            )}
            <div className="stat-item">
              <p className="stat-label">Wins</p>
              <p className="stat-value">{stats.wins}</p>
            </div>
            <div className="stat-item">
              <p className="stat-label">Losses</p>
              <p className="stat-value">{stats.losses}</p>
            </div>
            <div className="stat-item">
              <p className="stat-label">Win Rate</p>
              <p className="stat-value">{winRate}%</p>
            </div>
            <div className="stat-item">
              <p className="stat-label">Total Games</p>
              <p className="stat-value">{stats.wins + stats.losses}</p>
            </div>
          </div>
        )}
      </div>

      {isAuthenticated && (
        <div className="profile-section">
          <h3>Account</h3>
          <p className="account-info">
            Logged in as: <strong>{user?.email}</strong>
          </p>

          <div className="danger-zone">
            <h4>Danger Zone</h4>
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="menu-btn danger small"
                disabled={deletingAccount}
              >
                Delete Account
              </button>
            ) : (
              <div className="delete-confirm">
                <p className="warning-text">
                  ⚠️ Are you sure? This will permanently delete your account, statistics, and all associated data. This action cannot be undone.
                </p>
                <div className="delete-buttons">
                  <button
                    onClick={handleDeleteAccount}
                    className="menu-btn danger small"
                    disabled={deletingAccount}
                  >
                    {deletingAccount ? 'Deleting...' : 'Yes, Delete My Account'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="menu-btn secondary small"
                    disabled={deletingAccount}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
